// @flow
import toLower from 'lodash/toLower'
import * as React from 'react'
import { type Question } from 'types/question'
import type { Response } from 'types/response'
import type { SessionObject } from 'types/session-object'
import TextField from 'ui/molecules/TextField'
import Select from 'ui/Select'
import { ZentakeContext } from 'utils/ZentakeContext'

import { TriggerUserActions } from '@/components/TriggerUserActions'

import { hasText } from '../../../utils/helpers'
import ZentakeFormContext from '../ZentakeFormContext'

type Props = {
  question: Question,
  isOfficeUse: boolean,
  response: Response,
  sessions?: SessionObject,
}

function InsuranceCarrierQuestion({
  question,
  isOfficeUse,
  response,
  sessions,
}: Props): React$Node {
  const {
    isQuestionReadonly,
    isQuestionDirty,
    isQuestionValid,
    onAnswerChanged,
    getQuestionError,
    getAnswer,
  } = React.useContext(ZentakeFormContext)
  const { isFetchingInsuranceCompanies, insuranceCompanies } =
    React.useContext(ZentakeContext)

  const readonly = isQuestionReadonly(question)

  const answer = getAnswer(question)
  const isDirty = isQuestionDirty(question)
  const isValid = isQuestionValid(question)
  const questionError = getQuestionError(question)

  const [hasError, setHasError] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState(
    'This field is required'
  )
  const [value, setValue] = React.useState(answer?.answer_raw)

  React.useEffect(() => {
    if (onAnswerChanged) {
      onAnswerChanged(question, value)
    }
    // eslint-disable-next-line
  }, [value])

  const options = React.useMemo(() => {
    return insuranceCompanies.map((company) => ({
      label: company.name,
      value: company.id,
    }))
  }, [insuranceCompanies])

  React.useEffect(() => {
    setHasError(
      (isDirty && question?.is_required && !isValid) || hasText(questionError)
    )
    setErrorMessage(
      questionError && typeof questionError === 'string'
        ? questionError
        : 'This field is required'
    )
  }, [isDirty, question.is_required, isValid, questionError])

  const handleChange = React.useCallback(
    (newValue: any, isCreating: boolean = true) => {
      if (
        isCreating &&
        options?.find(
          (option) =>
            option.label !== newValue &&
            toLower(option.label).includes(toLower(newValue))
        )
      ) {
        setValue('')
      } else {
        setValue(newValue)
      }
    },
    [options]
  )

  const readonlyAnswer = React.useMemo(() => {
    if (!value) {
      return ''
    }

    // eslint-disable-next-line eqeqeq
    const option = options?.find((option) => option.value == value)
    return option ? option.label : ''
  }, [options, value])
  return (
    <div>
      {readonly ? (
        <TextField
          disabled
          isOfficeUse={isOfficeUse}
          isRequired={question.is_required}
          label={question.text}
          value={readonlyAnswer}
        />
      ) : (
        <TriggerUserActions>
          <Select
            showOptions
            data-form-id={response?.form?.id}
            data-packet-id={
              sessions?.fillResponseCustomer?.packetResponse?.packet?.id
            }
            data-response-id={response?.id}
            disabled={readonly}
            errorLabel={errorMessage}
            hasError={hasError}
            isOfficeUse={isOfficeUse}
            isRequired={question.is_required}
            label={question.text}
            loading={isFetchingInsuranceCompanies}
            onChange={(option) => handleChange(option?.value, false)}
            onCreate={handleChange}
            options={options}
            value={options?.find((option) => option.value === value)}
          />
        </TriggerUserActions>
      )}
    </div>
  )
}

export default InsuranceCarrierQuestion
